'use strict';

//import Chart from 'chart.js/auto';

// -----------------------------
// Configuration
// -----------------------------
const SETTINGS = {
  PARTICLE_COUNT: 80,
  MOUSE_RADIUS: 150,
  EASING: 0.05,
  COLORS: ['#00F0FF', '#7000FF'],
  BG_ALPHA: 0.15,
  STATE_NAMES: [
    'GENOMICS ANALYTICS',
    'COMPILER OPTIMIZATION',
    'INTERACTIVE C++',
    'BIOLOGICAL MODELS',
    'PHYSICS KERNELS'
  ],
  // Helix settings
  HELIX_RADIUS: 60,
  HELIX_SPACING: 10,
  // Graph / Vortex settings
  GRAPH_BASE_RADIUS: 60,
  VORTEX_SPEED: 0.5
};

// Sequence: 0 (Genomics) -> Random (1-4) -> 0 (Genomics)
const sequence = [0, Math.floor(Math.random() * 4) + 1, 0];
let sequenceIndex = 0;

// -----------------------------
// Core state
// -----------------------------
let canvas = null;
let ctx = null;
const mouse = { x: -100, y: -100 };
let particles = [];
let currentState = 0;

// -----------------------------
// Utilities
// -----------------------------
const rand = (min = 0, max = 1) => Math.random() * (max - min) + min;

const createParticle = (canvasWidth, canvasHeight) => ({
  x: rand(0, canvasWidth),
  y: rand(0, canvasHeight),
  baseX: rand(0, canvasWidth),
  baseY: rand(0, canvasHeight),
  size: rand(0.5, 2.5),
  density: rand(1, 31),
  color: SETTINGS.COLORS[Math.floor(Math.random() * SETTINGS.COLORS.length)]
});

const debounce = (fn, wait = 120) => {
  let t;
  return function (...args) {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
};

// -----------------------------
// Initialization
// -----------------------------
function init() {
  if (!canvas) return;

  canvas.width = window.innerWidth;
  canvas.height = window.innerHeight;

  particles = new Array(SETTINGS.PARTICLE_COUNT)
    .fill(null)
    .map(() => createParticle(canvas.width, canvas.height));
}

// -----------------------------
// Input handlers
// -----------------------------
window.addEventListener('mousemove', (e) => {
  // prefer clientX/clientY for consistency
  mouse.x = e.clientX;
  mouse.y = e.clientY;
});

window.addEventListener('resize', debounce(() => {
  if (canvas) init();
}, 120));

// -----------------------------
// Animation / Draw loop
// -----------------------------
function draw() {
  if (!ctx) return;

  // background
  ctx.fillStyle = `rgba(255, 255, 255, ${SETTINGS.BG_ALPHA})`;
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  const centerX = canvas.width * 0.5;
  const centerY = canvas.height * 0.9;
  const time = Date.now() / 1000;

  particles.forEach((p, i) => {
    let tx;
    let ty;

    // State-based target position
    switch (currentState) {
      case 0: // Interactive Cloud
        tx = p.baseX;
        ty = p.baseY;
        break;

      case 1: // DNA Helix
        {
          const layer = i % 2 === 0 ? 1 : -1;
          tx = centerX + Math.sin(time + i * 0.2) * SETTINGS.HELIX_RADIUS * layer;
          ty = (centerY - 200) + (i * SETTINGS.HELIX_SPACING) % 400;
        }
        break;

      case 2: // Compiler Graph Nodes
        {
          const gAngle = i * 0.1;
          const gR = SETTINGS.GRAPH_BASE_RADIUS + Math.sin(time * 0.5 + i) * 20;
          tx = centerX + Math.cos(gAngle) * gR;
          ty = centerY + Math.sin(gAngle) * gR;
        }
        break;

      case 3: // Biological Cells (rings)
        {
          const ring = Math.floor(i / 30);
          const cAngle = (i % 30) * (Math.PI * 2 / 30);
          const radius = ring * 40 + 20;
          tx = centerX + Math.cos(cAngle + time * SETTINGS.VORTEX_SPEED) * radius;
          ty = centerY + Math.sin(cAngle + time * SETTINGS.VORTEX_SPEED) * radius;
        }
        break;

      default: // Physics / Vortex
        {
          const vAngle = i * 0.05 + time;
          const vR = i * 2;
          tx = centerX + Math.cos(vAngle) * vR;
          ty = centerY + Math.sin(vAngle) * vR;
        }
    }

    // Mouse interaction physics
    const dx = mouse.x - p.x;
    const dy = mouse.y - p.y;
    const distance = Math.sqrt(dx * dx + dy * dy);

    if (distance < SETTINGS.MOUSE_RADIUS) {
      const forceDirectionX = dx / distance;
      const forceDirectionY = dy / distance;
      const force = (SETTINGS.MOUSE_RADIUS - distance) / SETTINGS.MOUSE_RADIUS;

      p.x -= forceDirectionX * force * p.density;
      p.y -= forceDirectionY * force * p.density;
    } else {
      p.x += (tx - p.x) * SETTINGS.EASING;
      p.y += (ty - p.y) * SETTINGS.EASING;
    }

    // render particle
    ctx.fillStyle = p.color;
    ctx.beginPath();
    ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
    ctx.fill();
  });

  requestAnimationFrame(draw);
}

// -----------------------------
// Smooth scrolling helper
// -----------------------------
function scrollToSection(id) {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: 'smooth' });
}

// -----------------------------
// DOM ready / App boot
// -----------------------------
document.addEventListener('DOMContentLoaded', () => {
  // Set the copyright year
  document.getElementById('year').textContent = new Date().getFullYear();

  canvas = document.getElementById('hero-canvas');

  if (!canvas) {
    console.warn('hero-canvas not found, animation disabled.');
    return;
  }

  ctx = canvas.getContext('2d');

  init();
  draw();
});

// -----------------------------
// Sequence state advancement
// -----------------------------
const stateInterval = setInterval(() => {
  sequenceIndex += 1;

  if (sequenceIndex < sequence.length) {
    currentState = sequence[sequenceIndex];
    console.log(`Switched to State: ${currentState}`);
  } else {
    clearInterval(stateInterval);
    console.log('Sequence finished at State 0.');
  }
}, 8000);

// -----------------------------
// Dashboard data and charting
// -----------------------------
const dashboardData = {
  biodynamo: {
    title: 'BioDynaMo: CART Cell Therapy',
    desc:
      'Migration of CART cell therapy simulations to the BioDynaMo engine resulted in significant performance gains. The optimized C++ backend handles agent-based simulations 2x faster than standard legacy implementations.',
    stat: '2.0x Speedup',
    chartType: 'bar',
    chartData: {
      labels: ['Legacy Implementation', 'BioDynaMo (Exajump)'],
      datasets: [{
        label: 'Simulation Speed (Normalized)',
        data: [1.0, 2.0],
        backgroundColor: ['#e7e5e4', '#0e7490'],
        borderRadius: 4,
        barPercentage: 0.6
      }]
    }
  },

  clad: {
    title: 'Clad: Auto-Differentiation',
    desc:
      'Clad provides automatic differentiation for C++ without the overhead of external libraries. In benchmarks against PyTorch CPU execution, Clad demonstrates a significant edge in execution time for gradient derivation.',
    stat: '-40% Latency',
    chartType: 'bar',
    chartData: {
      labels: ['PyTorch (CPU)', 'Clad (Optimized)'],
      datasets: [{
        label: 'Execution Time (Lower is Better)',
        data: [100, 60],
        backgroundColor: ['#e7e5e4', '#0e7490'],
        borderRadius: 4,
        barPercentage: 0.6
      }]
    }
  },

  cppjit: {
    title: 'CppJit: Enhanced Cppyy',
    desc:
      'CppJit removes domain-specific dependencies from cppyy, offering a lightweight JIT solution. It outperforms Numba-CUDA in flexibility for complex data types and is currently being evaluated by major Fintech players.',
    stat: 'Production Ready',
    chartType: 'radar',
    chartData: {
      labels: ['Speed', 'Flexibility', 'Dependency Weight', 'Ease of Use', 'Fintech Suitability'],
      datasets: [{
        label: 'CppJit',
        data: [90, 95, 20, 85, 90],
        backgroundColor: 'rgba(14, 116, 144, 0.5)',
        borderColor: '#0e7490',
        pointBackgroundColor: '#fff'
      }, {
        label: 'Standard Alternatives',
        data: [85, 60, 50, 70, 60],
        backgroundColor: 'rgba(231, 229, 228, 0.5)',
        borderColor: '#a8a29e',
        pointBackgroundColor: '#fff'
      }]
    }
  }
};

let performanceChart = null;

function initChart(key) {
  const canvasEl = document.getElementById('performanceChart');
  if (!canvasEl) return;

  const chartCtx = canvasEl.getContext('2d');
  const data = dashboardData[key];

  if (performanceChart) performanceChart.destroy();

  const isRadar = data.chartType === 'radar';

  const config = {
    type: data.chartType,
    data: data.chartData,
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: isRadar ? true : false,
          position: 'bottom'
        },
        tooltip: {
          backgroundColor: '#1c1917',
          padding: 12,
          titleFont: { family: 'Inter', size: 14 },
          bodyFont: { family: 'Space Mono', size: 12 }
        }
      },
      scales: isRadar
        ? {
            r: {
              angleLines: { color: '#e5e7eb' },
              grid: { color: '#e5e7eb' },
              pointLabels: { font: { family: 'Inter', size: 12 } },
              suggestedMin: 0,
              suggestedMax: 100
            }
          }
        : {
            y: {
              beginAtZero: true,
              grid: { color: '#f5f5f4' },
              ticks: { font: { family: 'Space Mono' } }
            },
            x: {
              grid: { display: false },
              ticks: { font: { family: 'Inter', weight: '600' } }
            }
          }
    }
  };

  performanceChart = new Chart(chartCtx, config);
}

// -----------------------------
// Dashboard interaction logic
// -----------------------------
function updateDashboard(key) {
  const data = dashboardData[key];
  if (!data) return;

  const titleEl = document.getElementById('tech-title');
  const descEl = document.getElementById('tech-desc');
  const statEl = document.getElementById('tech-stat');

  if (titleEl) titleEl.innerText = data.title;
  if (descEl) descEl.innerText = data.desc;
  if (statEl) statEl.innerText = data.stat;

  initChart(key);

  document.querySelectorAll('.tab-btn').forEach((btn) => {
    btn.classList.remove('bg-white', 'shadow-inner', 'border-b-4', 'border-cyan-700');
    btn.classList.add('hover:bg-white');

    if (btn.id === `btn-${key}`) {
      btn.classList.add('bg-white', 'border-b-4', 'border-cyan-700');
      btn.classList.remove('hover:bg-white');
    }
  });
}

document.addEventListener('DOMContentLoaded', () => {
  // attach benchmark buttons
  const benchmarkButtons = document.querySelectorAll('[data-benchmark]');
  benchmarkButtons.forEach((btn) => {
    btn.addEventListener('click', () => {
      const key = btn.getAttribute('data-benchmark');
      updateDashboard(key);
    });
  });

  // initial load
  updateDashboard('biodynamo');
});

// -----------------------------
// Terminal simulation (typewriter)
// -----------------------------
const terminalLines = [
  { text: '#include <iostream>', delay: 50 },
  { text: '#include <vector>', delay: 50 },
  { text: 'auto process_data(int n) {', delay: 50 },
  { text: '  std::vector<double> v(n, 3.14);', delay: 50 },
  { text: '  return v.size();', delay: 50 },
  { text: '}', delay: 50 },
  { text: 'process_data(1000)', delay: 800 },
  { text: '// Output: (int) 1000', delay: 0, isResult: true }
];

// Intersection observer options
const observerOptions = {
  root: null,
  threshold: 0.3
};

const terminalObserver = new IntersectionObserver((entries, obs) => {
  entries.forEach((entry) => {
    if (entry.isIntersecting) {
      startTerminal();
      obs.unobserve(entry.target);
    }
  });
}, observerOptions);

// Observe terminal container if present
const terminalTarget = document.getElementById('terminal-content');
if (terminalTarget) terminalObserver.observe(terminalTarget);

async function startTerminal() {
  const container = document.getElementById('typewriter-output');
  if (!container) return;

  // Clear previous content if any (optional, prevents duplication on re-entry)
  container.innerHTML = '';

  await new Promise((r) => setTimeout(r, 1000));

  for (const line of terminalLines) {
    const lineDiv = document.createElement('div');
    lineDiv.className = line.isResult ? 'text-cyan-400 font-bold mt-2 mb-4' : 'text-stone-300';
    container.appendChild(lineDiv);

    if (line.isResult) {
      lineDiv.innerText = line.text;
    } else {
      // Optimized typewriter: textContent avoids HTML parsing overhead
      for (const ch of line.text) {
        lineDiv.textContent += ch;
        await new Promise((r) => setTimeout(r, Math.random() * 30 + 20));
      }
    }

    const termContent = document.getElementById('terminal-content');
    if (termContent) termContent.scrollTop = termContent.scrollHeight;

    await new Promise((r) => setTimeout(r, line.delay));
  }
}
